/**
 * src/services/attendance.api.ts 의 짝.
 *
 * 휴가와 일정 공유(외근·출장·기타) 모두 결재를 받는 신청이다. 휴가의 자동 승인은 연차를 깎는
 * 유형에서 설정의 일수 상한과 신청 기한을 모두 충족할 때만이고, 남은 연차보다 긴 신청은 거절한다.
 * 일정 공유에는 자동 승인이 없다.
 *
 * 휴가 유형은 사무소가 정한다(`/vacation-kinds/`). 하루보다 짧은 유형(반차·반반차)은 그 하루의
 * 시각 범위로 받고, 길이가 하루 기준 시간 × 유형의 날수와 맞아야 한다. 차감 일수는 서버가 센다 —
 * 종일은 근무일 수, 시각 범위는 그 시간 ÷ 하루 기준 시간(점심을 빼지 않는다).
 *
 * 휴가 유형과 일정 공유의 결재는 아직 백엔드에 없다 — 여기 handler 가 계약 초안이다.
 */
import { differenceInCalendarDays, parseISO, startOfISOWeek } from "date-fns";
import { HttpResponse, http } from "msw";
import { today } from "@/lib/dates";
import { vacationDays } from "@/lib/leave";
import type {
  AttendanceDecision,
  GrantInput,
  GrantPatch,
  HolidayInput,
  ScheduleCategory,
  ScheduleCategoryInput,
  ScheduleCategoryPatch,
  ScheduleInput,
  UserSchedule,
  Vacation,
  VacationGrant,
  VacationInput,
} from "@/types";
import { VACATION_TYPE_LABEL } from "../data/attendance";
import { db } from "../db";
import { fail, latency, requireHr, requireUser } from "../http";

const BASE = "/api/v1";
let seq = 0;

const holidayDates = () => new Set(db.holidays.map((holiday) => holiday.holiday_date));

const standardHoursOf = (userId: string) =>
  db.users.find((u) => u.id === userId)?.standard_daily_hours ?? 8;

const nameOf = (userId: string) => db.users.find((u) => u.id === userId)?.name ?? userId;

/** 서버처럼 시간대 없이 온 시각은 사무소 시각(+09:00)으로 읽는다. */
const inOffice = (moment: string) =>
  /[+-]\d{2}:\d{2}$|Z$/.test(moment) ? moment : `${moment.slice(0, 19)}+09:00`;

/** 그 달에 하루라도 걸치는가 — 달력은 달마다 받는다. */
const inMonth = (start: string, end: string, year: number, month: number) => {
  const key = `${year}-${String(month).padStart(2, "0")}`;
  return start.slice(0, 7) <= key && key <= end.slice(0, 7);
};

/** 달력용 — 사유와 결정 내용을 뺀 사본. */
const toCalendar = (vacation: Vacation) => ({
  id: vacation.id,
  user_id: vacation.user_id,
  user_name: vacation.user_name,
  type: vacation.type,
  type_label: vacation.type_label,
  start_date: vacation.start_date,
  end_date: vacation.end_date,
  start_time: vacation.start_time,
  end_time: vacation.end_time,
  days: vacation.days,
});

/** 새 일정 카테고리는 맨 끝이다. */
const nextOrder = () => Math.max(0, ...db.scheduleCategories.map((row) => row.sort_order)) + 10;

/** 연차를 깎는 유형인가 — 서버처럼 연차만 깎고 기타는 깎지 않는다. */
const deducts = (type: string) => type === "annual";

/** 부여 한 건의 사용과 잔여 — 그 기간에 든 승인·대기 휴가 중 연차를 깎는 유형을 센다. */
function withUsage(grant: Omit<VacationGrant, "used" | "remaining">): VacationGrant {
  const used = db.vacations
    .filter(
      (vacation) =>
        vacation.user_id === grant.user_id &&
        deducts(vacation.type) &&
        (vacation.status === "approved" || vacation.status === "pending") &&
        vacation.start_date >= grant.period_from &&
        vacation.start_date < grant.period_to
    )
    .reduce((sum, vacation) => sum + vacation.days, 0);
  return {
    ...grant,
    used: Math.round(used * 100) / 100,
    remaining: Math.round((grant.granted + grant.adjusted - used) * 100) / 100,
  };
}

const remainingOf = (userId: string) =>
  db.vacationGrants
    .filter((grant) => grant.user_id === userId)
    .reduce((sum, grant) => sum + withUsage(grant).remaining, 0);

/** 자동 승인인가 — 일수 상한과 신청 기한을 모두 충족해야 한다. 상한이 0 이면 늘 승인을 받는다. */
function autoApproves(days: number, startDate: string): boolean {
  const config = db.siteConfig;
  if (config.vacation_auto_approve_days <= 0) return false;
  if (days > config.vacation_auto_approve_days) return false;
  const start = parseISO(startDate);
  const now = parseISO(today());
  return config.vacation_notice_prev_week
    ? startOfISOWeek(start) > startOfISOWeek(now)
    : differenceInCalendarDays(start, now) >= config.vacation_notice_days;
}

const HALF_HOUR = /^([01]\d|2[0-3]):(00|30)(:00)?$/;

const minutesOf = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

export const attendanceHandlers = [
  /* ── 휴가 ────────────────────────────────────────────────────────── */

  http.get(`${BASE}/vacations/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.vacations
      .filter((vacation) => vacation.user_id === user.id)
      .sort((a, b) => (a.start_date < b.start_date ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/vacations/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const input = (await request.json()) as VacationInput;
    if (input.type !== "annual" && input.type !== "other")
      return fail(422, "없는 휴가 유형입니다.");
    if (!input.start_date || !input.end_date) return fail(422, "기간을 고르세요.");
    if (input.end_date < input.start_date) return fail(422, "기간의 끝이 시작보다 빨라요.");
    const standard = standardHoursOf(user.id);
    // 서버처럼 하루보다 짧게 쓰면 시작·종료 시각을 받는다 — 반차·반반차는 유형이 아니라 시각 범위다.
    const partial = !!(input.start_time && input.end_time);
    if (partial) {
      if (input.start_date !== input.end_date)
        return fail(422, "시각을 고르면 하루만 쓸 수 있어요.");
      if (!HALF_HOUR.test(input.start_time!) || !HALF_HOUR.test(input.end_time!))
        return fail(422, "시각은 30분 단위로 고르세요.");
      if (minutesOf(input.end_time!) <= minutesOf(input.start_time!))
        return fail(422, "종료 시각이 시작보다 빨라요.");
    }
    const days = vacationDays(
      {
        start_date: input.start_date,
        end_date: input.end_date,
        start_time: partial ? input.start_time : null,
        end_time: partial ? input.end_time : null,
      },
      standard,
      holidayDates()
    );
    if (days <= 0) return fail(422, "고른 기간에 근무일이 없어요.");
    if (deducts(input.type) && days > remainingOf(user.id))
      return fail(422, "남은 연차보다 많아요.");

    const auto = deducts(input.type) && autoApproves(days, input.start_date);
    const stamp = new Date().toISOString();
    const made: Vacation = {
      id: `vacation-new-${++seq}`,
      user_id: user.id,
      user_name: nameOf(user.id),
      type: input.type,
      type_label: VACATION_TYPE_LABEL[input.type],
      start_date: input.start_date,
      end_date: input.end_date,
      start_time: partial ? `${input.start_time!.slice(0, 5)}:00` : null,
      end_time: partial ? `${input.end_time!.slice(0, 5)}:00` : null,
      days,
      reason: input.reason?.trim() ?? "",
      status: auto ? "approved" : "pending",
      auto_approved: auto,
      approver_id: null,
      decided_at: auto ? stamp : null,
      decision_note: "",
      cancel_requested_at: null,
      created_at: stamp,
    };
    db.vacations.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.get(`${BASE}/vacations/calendar/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const query = new URL(request.url).searchParams;
    const year = Number(query.get("year"));
    const month = Number(query.get("month"));
    const results = db.vacations
      .filter(
        (vacation) =>
          vacation.status === "approved" &&
          inMonth(vacation.start_date, vacation.end_date, year, month)
      )
      .map(toCalendar);
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/vacations/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.vacations.find((v) => v.id === params.id && v.user_id === user.id);
    return found ? HttpResponse.json(found) : fail(404, "없는 휴가입니다.");
  }),

  http.post(`${BASE}/vacations/:id/cancel/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.vacations.find((v) => v.id === params.id && v.user_id === user.id);
    if (!found) return fail(404, "없는 휴가입니다.");
    // 승인 대기는 바로 취소되고, 승인된 건은 취소 신청으로 바뀐다.
    if (found.status === "pending") found.status = "cancelled";
    else if (found.status === "approved") found.cancel_requested_at = new Date().toISOString();
    else return fail(409, "취소할 수 없는 상태예요.");
    return HttpResponse.json(found);
  }),

  /* ── 일정 카테고리 — 사무소가 늘린다 ───────────────────────────────── */

  http.get(`${BASE}/schedule-categories/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = [...db.scheduleCategories].sort((a, b) => a.sort_order - b.sort_order);
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/schedule-categories/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as ScheduleCategoryInput;
    const name = input.name.trim();
    if (!name) return fail(422, "이름을 적어 주세요.");
    if (db.scheduleCategories.some((row) => row.name === name))
      return fail(409, "같은 이름의 카테고리가 있습니다.");
    const made: ScheduleCategory = {
      id: `cat-${crypto.randomUUID().slice(0, 8)}`,
      name,
      color: input.color,
      sort_order: nextOrder(),
    };
    db.scheduleCategories.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.patch(`${BASE}/admin/schedule-categories/:id/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const category = db.scheduleCategories.find((row) => row.id === params.id);
    if (!category) return fail(404, "없는 일정 카테고리입니다.");
    const patch = (await request.json()) as ScheduleCategoryPatch;
    if (
      patch.name &&
      db.scheduleCategories.some((row) => row.id !== category.id && row.name === patch.name)
    )
      return fail(409, "같은 이름의 카테고리가 있습니다.");
    Object.assign(category, patch);
    return HttpResponse.json(category);
  }),

  http.delete(`${BASE}/admin/schedule-categories/:id/`, async ({ params }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const index = db.scheduleCategories.findIndex((row) => row.id === params.id);
    if (index < 0) return fail(404, "없는 일정 카테고리입니다.");
    // 쓰는 일정이 있으면 서버는 행을 남긴 채 숨긴다. mock 은 목록에서만 빼면 같은 결과다.
    db.scheduleCategories.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 일정 공유 — 외근·출장·기타. 휴가처럼 결재를 받는다 ──────────── */

  http.get(`${BASE}/schedules/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.schedules
      .filter((schedule) => schedule.user_id === user.id)
      .sort((a, b) => (a.starts_at < b.starts_at ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/schedules/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const input = (await request.json()) as ScheduleInput;
    if (!input.reason?.trim()) return fail(422, "사유를 적어 주세요.");
    if (input.ends_at <= input.starts_at) return fail(422, "종료가 시작보다 앞이에요.");
    if (
      !HALF_HOUR.test(input.starts_at.slice(11, 16)) ||
      !HALF_HOUR.test(input.ends_at.slice(11, 16))
    )
      return fail(422, "시각은 30분 단위로 고르세요.");
    const category = db.scheduleCategories.find((row) => row.id === input.category_id);
    if (!category) return fail(404, "없는 일정 카테고리입니다.");
    const made: UserSchedule = {
      id: `schedule-new-${++seq}`,
      user_id: user.id,
      user_name: nameOf(user.id),
      category_id: category.id,
      category_name: category.name,
      category_color: category.color,
      starts_at: inOffice(input.starts_at),
      ends_at: inOffice(input.ends_at),
      project_id: input.project_id ?? null,
      reason: input.reason.trim(),
      // 일정 공유는 결재가 없다 — 넣으면 바로 확정이다.
      cancelled_at: null,
      created_at: new Date().toISOString(),
    };
    db.schedules.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.get(`${BASE}/schedules/calendar/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const query = new URL(request.url).searchParams;
    const year = Number(query.get("year"));
    const month = Number(query.get("month"));
    const results = db.schedules.filter(
      (schedule) =>
        !schedule.cancelled_at &&
        inMonth(schedule.starts_at.slice(0, 10), schedule.ends_at.slice(0, 10), year, month)
    );
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/schedules/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.schedules.find((s) => s.id === params.id && s.user_id === user.id);
    return found ? HttpResponse.json(found) : fail(404, "없는 일정입니다.");
  }),

  http.post(`${BASE}/schedules/:id/cancel/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.schedules.find((s) => s.id === params.id && s.user_id === user.id);
    if (!found) return fail(404, "없는 일정입니다.");
    // 일정은 결재 없이 바로 취소된다. 행은 남기고 취소 시각만 채운다.
    if (found.cancelled_at) return fail(409, "이미 취소한 일정이에요.");
    found.cancelled_at = new Date().toISOString();
    return HttpResponse.json(found);
  }),

  /* ── 휴일 ────────────────────────────────────────────────────────── */

  http.get(`${BASE}/holidays/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const year = new URL(request.url).searchParams.get("year");
    const results = db.holidays
      .filter((holiday) => holiday.holiday_date.startsWith(String(year)))
      .sort((a, b) => a.holiday_date.localeCompare(b.holiday_date));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/holidays/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as HolidayInput;
    if (!input.holiday_date || !input.name?.trim()) return fail(422, "날짜와 이름을 적어 주세요.");
    if (db.holidays.some((holiday) => holiday.holiday_date === input.holiday_date))
      return fail(409, "그날은 이미 휴일이에요.");
    const made = {
      holiday_date: input.holiday_date,
      name: input.name.trim(),
      kind: input.kind,
    };
    db.holidays.push(made);
    db.holidays.sort((a, b) => a.holiday_date.localeCompare(b.holiday_date));
    return HttpResponse.json(made, { status: 201 });
  }),

  http.delete(`${BASE}/admin/holidays/:date/`, async ({ params }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const index = db.holidays.findIndex((holiday) => holiday.holiday_date === params.date);
    if (index < 0) return fail(404, "없는 휴일입니다.");
    db.holidays.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 연차 부여 ───────────────────────────────────────────────────── */

  http.get(`${BASE}/vacation-grants/me/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.vacationGrants.filter((grant) => grant.user_id === user.id).map(withUsage);
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/admin/vacation-grants/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const userId = new URL(request.url).searchParams.get("user_id");
    const results = db.vacationGrants
      .filter((grant) => !userId || grant.user_id === userId)
      .map(withUsage)
      .sort((a, b) => a.user_name.localeCompare(b.user_name, "ko"));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/vacation-grants/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as GrantInput;
    if (!input.user_id) return fail(422, "직원을 고르세요.");
    if (input.period_to <= input.period_from) return fail(422, "기간을 확인해 주세요.");
    if ((input.adjusted ?? 0) !== 0 && !input.note?.trim())
      return fail(422, "조정에는 사유가 필요해요.");
    // 같은 직원의 부여 기간은 겹칠 수 없다.
    if (
      db.vacationGrants.some(
        (grant) =>
          grant.user_id === input.user_id &&
          grant.period_from < input.period_to &&
          input.period_from < grant.period_to
      )
    )
      return fail(409, "그 기간에 겹치는 부여가 있어요.");
    const made = {
      id: `grant-new-${++seq}`,
      user_id: input.user_id,
      user_name: nameOf(input.user_id),
      period_from: input.period_from,
      period_to: input.period_to,
      granted: input.granted,
      adjusted: input.adjusted ?? 0,
      note: input.note?.trim() ?? "",
    };
    db.vacationGrants.push(made);
    return HttpResponse.json(withUsage(made), { status: 201 });
  }),

  http.patch(`${BASE}/admin/vacation-grants/:id/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const found = db.vacationGrants.find((grant) => grant.id === params.id);
    if (!found) return fail(404, "없는 부여입니다.");
    const patch = (await request.json()) as GrantPatch;
    if (patch.granted !== undefined) found.granted = patch.granted;
    if (patch.adjusted !== undefined) found.adjusted = patch.adjusted;
    if (patch.note !== undefined) found.note = patch.note.trim();
    if (found.adjusted !== 0 && !found.note) return fail(422, "조정에는 사유가 필요해요.");
    return HttpResponse.json(withUsage(found));
  }),

  /* ── 관리자 휴가 결정 ────────────────────────────────────────────── */

  http.get(`${BASE}/admin/vacations/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const query = new URL(request.url).searchParams;
    const status = query.get("status");
    const userId = query.get("user_id");
    const cancelRequested = query.get("cancel_requested");
    const results = db.vacations
      .filter((vacation) => !status || vacation.status === status)
      .filter((vacation) => !userId || vacation.user_id === userId)
      .filter(
        (vacation) =>
          cancelRequested === null ||
          (cancelRequested === "true") === (vacation.cancel_requested_at !== null)
      )
      .sort((a, b) => (a.start_date < b.start_date ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/vacations/:id/decision/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const found = db.vacations.find((vacation) => vacation.id === params.id);
    if (!found) return fail(404, "없는 휴가입니다.");
    if (found.status !== "pending") return fail(409, "이미 처리된 신청이에요.");
    const decision = (await request.json()) as AttendanceDecision;
    if (!decision.approve && !decision.note?.trim()) return fail(422, "반려 사유를 적어 주세요.");
    found.status = decision.approve ? "approved" : "rejected";
    found.approver_id = admin.id;
    found.decided_at = new Date().toISOString();
    found.decision_note = decision.note?.trim() ?? "";
    return HttpResponse.json(found);
  }),

  http.post(`${BASE}/admin/vacations/:id/cancel-decision/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const found = db.vacations.find((vacation) => vacation.id === params.id);
    if (!found) return fail(404, "없는 휴가입니다.");
    if (!found.cancel_requested_at) return fail(409, "취소 신청이 없어요.");
    const decision = (await request.json()) as AttendanceDecision;
    if (!decision.approve && !decision.note?.trim()) return fail(422, "반려 사유를 적어 주세요.");
    if (decision.approve) found.status = "cancelled";
    found.cancel_requested_at = null;
    found.decision_note = decision.note?.trim() ?? "";
    return HttpResponse.json(found);
  }),
];
